import { useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import { InfoCircle } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import TierInfo from "components/TierInfo";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import Callout from "ds/components/Callout";
import FeedbackActions from "ds/components/Feedback/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { BillingTierFeature } from "types/generated";
import { isSaasDistribution } from "utils/distribution";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";
import { AccountContext } from "views/AccountWrapper";

import { SettingsContext } from "../Context";
import { UserManagementActivationStatus } from "../types";
import { SSO_BACKUP_CREDENTIALS_KEY, SSO_SAML_INFO_KEY, SSO_SETTINGS_INFO_KEY } from "./constants";
import { GET_SSO_SETTINGS } from "./gql";
import SettingsSingleSignOnOIDC from "./OIDC";
import SettingsSingleSignOnSAML from "./SAML";
import SettingsSingleSignOnScim from "./Scim";

const isSaaS = isSaasDistribution();

const SettingsSingleSignOn = () => {
  const { accountName } = useTypedContext(AccountContext);
  useTitle(`Organization Settings · Single sign-on · ${accountName}`);

  const isTierContainSAML = useTierFeature(BillingTierFeature.CustomSsoSaml);
  const isTierContainSCIM = useTierFeature(BillingTierFeature.Scim);

  const navigate = useNavigate();
  const { activationStatus } = useTypedContext(SettingsContext);
  const { isTrial } = useTypedContext(SubscriptionContext);

  const { loading, data, error } = useQuery(GET_SSO_SETTINGS);

  const isUserManagementActive = activationStatus === UserManagementActivationStatus.ACTIVE;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSSO,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  if (!data) {
    return <NotFoundPage />;
  }

  const hasSSOConfig = data.hasSSO;
  const hasSAMLConfig = !!data.samlSettings;
  const hasSCIMConfig = !!data.scimSettings;
  const hasManagedUsers = data.managedUsers?.length > 0;

  // Page-level callouts cover the cases where SAML + SCIM are in the same state
  // (both not configured -> upgrade, both configured -> unsupported).
  const showAccountUpgradeCallout =
    isSaaS && !isTierContainSAML && !isTierContainSCIM && !hasSAMLConfig && !hasSCIMConfig;
  const showAccountUnsupportedCallout =
    isSaaS && !isTierContainSAML && !isTierContainSCIM && hasSAMLConfig && hasSCIMConfig;

  // Per-card banners cover every "out of tier" case the page-level callouts
  // don't. The account callouts only appear when BOTH features are out of tier,
  // so a per-card banner must show whenever the other feature is in tier (or in
  // the opposite config state) — otherwise the account callout would never run
  // and the user would see nothing.
  // Upgrade -> feature not in tier and not configured yet (invite to upgrade).
  // Unsupported -> feature not in tier but already configured (can't change it).
  const showSAMLUpgradeBanner =
    isSaaS && !isTierContainSAML && !hasSAMLConfig && (hasSCIMConfig || isTierContainSCIM);
  const showSAMLUnsupportedBanner =
    isSaaS && !isTierContainSAML && hasSAMLConfig && (!hasSCIMConfig || isTierContainSCIM);
  const showSCIMUpgradeBanner =
    isSaaS && !isTierContainSCIM && !hasSCIMConfig && (hasSAMLConfig || isTierContainSAML);
  const showSCIMUnsupportedBanner =
    isSaaS && !isTierContainSCIM && hasSCIMConfig && (!hasSAMLConfig || isTierContainSAML);

  return (
    <>
      {showAccountUpgradeCallout && (
        <TierInfo
          variant="promo"
          type="callout"
          feature={BillingTierFeature.CustomSsoSaml}
          title="Upgrade account to use SAML SSO 2.0 and SCIM"
        >
          Add extra layer of security to your account by enabling SAML SSO 2.0 or automatically
          provision and manage users and groups from your identity provider by enabling SCIM.
        </TierInfo>
      )}
      {isUserManagementActive && (
        <Callout
          variant="warning"
          title="Identity Access Management is active"
          storageKey={SSO_SETTINGS_INFO_KEY}
          closeCallback={() => trackSegmentAnalyticsEvent("Callout Close")}
        >
          Please note that switching to single sign-on will invalidate all current user and group
          mapping rules.
        </Callout>
      )}
      {showAccountUnsupportedCallout && (
        <TierInfo
          type="callout"
          variant="danger"
          feature={BillingTierFeature.CustomSsoSaml}
          title="SAML SSO 2.0 and SCIM are not supported by your plan"
        >
          You can still use or disable the SAML SSO 2.0 and SCIM, but to make changes, please
          upgrade to the <strong>Enterprise</strong> plan.
        </TierInfo>
      )}
      {isTrial && (
        <Callout
          variant="warning"
          title="You are using the trial version"
          storageKey={SSO_SAML_INFO_KEY}
        >
          Once your trial has expired and your plan is no longer Enterprise it will not be possible
          to update the SAML and SCIM configuration.
        </Callout>
      )}

      {!hasSSOConfig && (
        <Callout
          variant="warning"
          title="Ensure you have a backup set of credentials configured."
          storageKey={SSO_BACKUP_CREDENTIALS_KEY}
        >
          This might be useful for SSO misconfigurations or outages.
          <FeedbackActions>
            <Button variant="contrast" onPress={() => navigate("/settings/api-keys")} size="small">
              Create an API key
            </Button>
          </FeedbackActions>
        </Callout>
      )}

      <PageInfoHeaderWrapper>
        <Box align="center" gap="medium">
          <PageInfoHeaderTitle title="Single Sign-on" />
          <ButtonIcon icon={InfoCircle} variant="ghost">
            SSO allows you to connect your Spacelift account to an external identity provider. This
            allows your users to use their existing user accounts when logging into Spacelift.
          </ButtonIcon>
        </Box>
      </PageInfoHeaderWrapper>

      <Box direction="column" padding="x-large" gap="x-large" limitWidth="medium">
        <SettingsSingleSignOnSAML
          integration={data.samlSettings || undefined}
          oidcAlreadyEnabled={!!data.oidcSettings}
          showUpgradeBanner={showSAMLUpgradeBanner}
          showUnsupportedBanner={showSAMLUnsupportedBanner}
        />
        <SettingsSingleSignOnOIDC
          hasManagedUsers={hasManagedUsers}
          integration={data.oidcSettings || undefined}
          samlAlreadyEnabled={hasSAMLConfig}
        />
        <SettingsSingleSignOnScim
          settings={data.scimSettings || undefined}
          showUpgradeBanner={showSCIMUpgradeBanner}
          showUnsupportedBanner={showSCIMUnsupportedBanner}
        />
      </Box>
    </>
  );
};

export default SettingsSingleSignOn;
